/**
 * App.tsx — app shell: sidebar nav + top bar + page routing + modal management.
 * No router; /auth-setup is handled by a path check.
 */

import React, { useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useLeads } from './contexts/LeadsContext';
import { Dashboard } from './components/Dashboard';
import { LeadsPage, ClientsTab, CLIENTS_TABS } from './components/LeadsPage';
import { LeadDetailPage, LeadTab, LEAD_TABS } from './components/LeadDetailPage';
import { CalendarPage } from './components/CalendarPage';
import { StatsPage } from './components/StatsPage';
import { SettingsPage, SettingsTab, settingsTabFromQuery } from './components/SettingsPage';
import { AccountingPage } from './components/AccountingPage';
import { StatementPrint } from './components/StatementPrint';
import { JobReviewPrint } from './components/JobReviewPrint';
import { MigrationPage } from './pages/MigrationPage';
import { AuthSetup } from './pages/AuthSetup';
import { EstimatePortal } from './pages/EstimatePortal';
import { DocumentPortal } from './pages/DocumentPortal';
import { BadgeBar, useBadgeCounts, followupBadgeOf } from './components/BadgeBar';
import { GlobalSearch } from './components/GlobalSearch';
import { AddLeadModal, AddBranchModal, AddTariffModal, AddFeeModal } from './components/Modals';
import { Icons } from './lib/icons';
import { armListScroll, disarmListScroll, noteListUrl } from './hooks/useListState';
import { usePhoneWidth } from './hooks/usePhoneWidth';
import { RingCentralDock } from './components/RingCentralDock';

// Follow-Ups + Notifications are no longer sidebar pages (N3); they are tabs
// inside All Clients (rendered by LeadsPage). Tab state lives in the URL.
type PageType = 'dashboard' | 'leads' | 'lead-detail' | 'calendar' | 'stats' | 'accounting' | 'settings' | 'migration';

const MAIN_NAV: { id: PageType; label: string; Icon: React.ComponentType<any> }[] = [
  { id: 'dashboard', label: 'Dashboard', Icon: Icons.dashboard },
  { id: 'leads', label: 'All Clients', Icon: Icons.clients },
  { id: 'calendar', label: 'Calendar', Icon: Icons.calendar },
  { id: 'stats', label: 'Activity Stats', Icon: Icons.stats },
];

const PAGE_TITLES: Record<PageType, string> = {
  dashboard: 'Dashboard',
  leads: 'All Clients',
  'lead-detail': 'Lead Details',
  calendar: 'Calendar',
  stats: 'Activity Stats',
  accounting: 'Accounting',
  settings: 'Settings',
  migration: 'Migration',
};

// The browser title on the Clients page follows its tab.
const CLIENTS_TAB_TITLES: Record<ClientsTab, string> = {
  all: 'All Clients',
  new: 'New Leads',
  'follow-ups': 'Follow-Ups',
  notifications: 'Notifications',
};

// ---- Minimal URL routing (no react-router) — keeps the /estimate + /auth-setup
// pathname-check pattern. Clients tabs live in the URL as /clients?tab=…; the old
// /notifications and /follow-ups paths redirect into the matching tab.
// Follow-Ups tab view controls (N4a) — whose list and which day. Defaults stay
// out of the URL; non-defaults are linkable and survive back/forward.
const FU_WHO_DEFAULT = 'mine';   // 'mine' | userId | 'all'
const FU_WHEN_DEFAULT = 'today'; // 'today' | 'all' | 'YYYY-MM-DD'
const STATS_REP_DEFAULT = '';    // '' = role default (admin: all reps; rep: self)
const SETTINGS_TAB_DEFAULT: SettingsTab = 'general';  // kept out of the URL, like the two above

const urlForPage = (page: PageType, tab: ClientsTab, who = FU_WHO_DEFAULT, when = FU_WHEN_DEFAULT, rep = STATS_REP_DEFAULT, stab: SettingsTab = SETTINGS_TAB_DEFAULT): string => {
  if (page !== 'leads') {
    let url = page === 'dashboard' ? '/' : `/${page}`;
    if (page === 'stats' && rep !== STATS_REP_DEFAULT) url += `?rep=${encodeURIComponent(rep)}`;
    // Settings sub-tab, same shape as ?rep= above. It has to be threaded here
    // rather than written by SettingsPage itself: the load effect below
    // canonicalises the URL with replaceState, so a tab this function did not
    // know about would be stripped off a pasted /settings?tab=pricing link.
    if (page === 'settings' && stab !== SETTINGS_TAB_DEFAULT) url += `?tab=${encodeURIComponent(stab)}`;
    return url;
  }
  let url = `/clients?tab=${tab}`;
  if (tab === 'follow-ups') {
    if (who !== FU_WHO_DEFAULT) url += `&who=${encodeURIComponent(who)}`;
    if (when !== FU_WHEN_DEFAULT) url += `&when=${encodeURIComponent(when)}`;
  }
  return url;
};

/**
 * The list page's own view params (filters, search, sort, page — hooks/
 * useListState), i.e. everything on /clients except what App itself writes.
 * Carried through App's rewrites of the URL so they are never stripped.
 */
const listExtras = (): string => {
  if (window.location.pathname !== '/clients') return '';
  const q = new URLSearchParams(window.location.search);
  ['tab', 'who', 'when'].forEach((k) => q.delete(k));
  const s = q.toString();
  return s ? `&${s}` : '';
};

/**
 * The lead sub-tab named by ?tab=, or the default.
 *
 * ?tab=email IS DELIBERATELY ALIASED, not dropped. There was an Email tab for a
 * day and links to it exist — in bookmarks, in the browser history, possibly in
 * a message someone sent a colleague. Email now lives in the Sales feed, so that
 * is where those links land. Falling through to the default instead would drop
 * them on Estimate, which is the one tab that does not contain what they asked
 * for.
 *
 * Used by BOTH readers below — the initial load and the popstate handler — so
 * back and forward behave the same as a fresh open.
 */
function leadTabFromQuery(raw: string | null): LeadTab {
  if (raw === 'email') return 'sales';
  // Texts had a tab for a day too (RingCentral step 5); texting is on Sales now.
  if (raw === 'texts') return 'sales';
  return (LEAD_TABS as string[]).includes(raw ?? '') ? (raw as LeadTab) : 'estimate';
}

function parseLocation(): { page: PageType; tab: ClientsTab; who: string; when: string; rep: string; settingsTab: SettingsTab } {
  const path = window.location.pathname;
  const base = { who: FU_WHO_DEFAULT, when: FU_WHEN_DEFAULT, rep: STATS_REP_DEFAULT, settingsTab: SETTINGS_TAB_DEFAULT };
  if (path === '/notifications') return { page: 'leads', tab: 'notifications', ...base };
  if (path === '/follow-ups') return { page: 'leads', tab: 'follow-ups', ...base };
  if (path === '/clients') {
    const q = new URLSearchParams(window.location.search);
    const t = q.get('tab');
    return {
      page: 'leads',
      tab: (CLIENTS_TABS as readonly string[]).includes(t ?? '') ? (t as ClientsTab) : 'all',
      who: q.get('who') || FU_WHO_DEFAULT,
      when: q.get('when') || FU_WHEN_DEFAULT,
      rep: STATS_REP_DEFAULT,
      settingsTab: SETTINGS_TAB_DEFAULT,
    };
  }
  const bare = path.replace(/^\//, '');
  if ((['calendar', 'stats', 'accounting', 'settings', 'migration'] as string[]).includes(bare)) {
    const q = new URLSearchParams(window.location.search);
    return {
      page: bare as PageType, tab: 'all', ...base,
      rep: bare === 'stats' ? (q.get('rep') || STATS_REP_DEFAULT) : STATS_REP_DEFAULT,
      settingsTab: bare === 'settings' ? settingsTabFromQuery(q.get('tab')) : SETTINGS_TAB_DEFAULT,
    };
  }
  return { page: 'dashboard', tab: 'all', ...base };
}

export default function App() {
  const { token, user, loading, logout } = useAuth();
  const { refresh: refreshShared } = useLeads();  // shared lead list (top-bar search + All Clients)
  const [currentPage, setCurrentPage] = useState<PageType>('dashboard');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  // PHONE (≤640px): no sidebar — a bottom bar instead, and a slim top bar whose
  // search opens as a full-width row. Desktop is unchanged.
  const phone = usePhoneWidth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // The desktop top bar's live counts, on the phone bottom bar (polled only at
  // phone width — BadgeBar polls on desktop).
  const [badgeCounts] = useBadgeCounts(phone && !!token);
  const [modals, setModals] = useState<Record<string, boolean>>({});
  const [modalData, setModalData] = useState<any>({});
  // Active All Clients tab; mirrored into the URL so badges can deep-link and
  // back/forward works.
  const [clientsTab, setClientsTab] = useState<ClientsTab>('all');
  // Follow-Ups view (N4a): whose list + which day. Mirrored into the URL.
  const [fuWho, setFuWho] = useState<string>(FU_WHO_DEFAULT);
  const [fuWhen, setFuWhen] = useState<string>(FU_WHEN_DEFAULT);
  const [statsRep, setStatsRep] = useState<string>(STATS_REP_DEFAULT);
  // Settings sub-tab, URL-owned (/settings?tab=…) like the lead file's.
  const [settingsTab, setSettingsTab] = useState<SettingsTab>(SETTINGS_TAB_DEFAULT);
  // Bumped on every navigation that should rebuild the list from its URL (a
  // fresh visit, Back/Forward, the lead file's back link): it keys LeadsPage.
  const [listNonce, setListNonce] = useState(0);
  // The list URL a lead was opened from, so the lead file's back link returns
  // to that exact view (filters, page) rather than a fresh list.
  const lastListUrl = React.useRef<string | null>(null);
  // Lead file sub-tab, URL-owned (/lead/:id?tab=…) so it survives refresh/back.
  const [leadTab, setLeadTab] = useState<LeadTab>('estimate');
  /**
   * The email the Messages dropdown asked for (G1d): open this lead's Sales feed
   * scrolled to it and expanded. `n` is bumped on every request so clicking the
   * SAME message twice re-focuses it — without it the second click changes no
   * state and the feed sits still, which reads as a broken link.
   */
  const [focusMessage, setFocusMessage] = useState<{ leadId: string; messageId: string; n: number } | null>(null);

  // Navigate to a page (and, for All Clients, a tab), syncing the URL. A fresh
  // navigation resets the Follow-Ups view to the defaults (Mine · Today) and the
  // list to its default view; `params` seed it (the top-bar "See all" → ?q=).
  const navigate = React.useCallback((page: PageType, tab: ClientsTab = 'all', replace = false, params?: Record<string, string>) => {
    disarmListScroll();
    setListNonce((n) => n + 1);
    setCurrentPage(page);
    setClientsTab(tab);
    setFuWho(FU_WHO_DEFAULT);
    setFuWhen(FU_WHEN_DEFAULT);
    setStatsRep(STATS_REP_DEFAULT);
    setSettingsTab(SETTINGS_TAB_DEFAULT);
    let url = urlForPage(page, tab);
    const extra = new URLSearchParams(Object.entries(params ?? {}).filter(([, v]) => v)).toString();
    if (extra) url += `${url.includes('?') ? '&' : '?'}${extra}`;
    if (replace) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);
  }, []);

  // Change the Follow-Ups view (who/when) without leaving the tab; URL-synced.
  const setFollowupView = React.useCallback((who: string, when: string) => {
    setFuWho(who);
    setFuWhen(when);
    window.history.pushState({}, '', urlForPage('leads', 'follow-ups', who, when) + listExtras());
    noteListUrl();
  }, []);

  // Change the Activity Stats "whose" selection; URL-synced (?rep=), consistent
  // with the follow-ups view model. '' = role default (kept out of the URL).
  const setStatsView = React.useCallback((rep: string) => {
    setStatsRep(rep);
    window.history.pushState({}, '', urlForPage('stats', 'all', FU_WHO_DEFAULT, FU_WHEN_DEFAULT, rep));
  }, []);

  // Change the Settings sub-tab; URL-synced (?tab=), same model as the two above.
  const setSettingsView = React.useCallback((tab: SettingsTab) => {
    setSettingsTab(tab);
    window.history.pushState({}, '', urlForPage('settings', 'all', FU_WHO_DEFAULT, FU_WHEN_DEFAULT, STATS_REP_DEFAULT, tab));
  }, []);

  // Jump to the Follow-Ups tab with a specific view (N4b: dashboard Tomorrow →
  // Mine · <date>). Unlike navigate(), this preserves the requested who/when.
  const goToFollowUps = React.useCallback((who: string, when: string) => {
    setSelectedLeadId(null);
    setCurrentPage('leads');
    setClientsTab('follow-ups');
    setFuWho(who);
    setFuWhen(when);
    window.history.pushState({}, '', urlForPage('leads', 'follow-ups', who, when));
  }, []);

  // On load: apply the URL (redirecting /notifications + /follow-ups to the
  // canonical /clients?tab=…). On back/forward: re-sync from the URL.
  React.useEffect(() => {
    const path = window.location.pathname;
    // The statement print page keeps its own URL (month, ?print=1).
    if (path.startsWith('/estimate/') || path.startsWith('/document/') || path === '/auth-setup' || path.startsWith('/accounting/statement/')) return;

    // Deep link to a lead file (E3 Item 4c: the Move Booked email button). Opens
    // lead-detail directly; no URL normalization so the id survives back/forward.
    const openLeadFromPath = (p: string): boolean => {
      const m = p.match(/^\/lead\/([^/]+)\/?$/);
      if (!m) return false;
      const t = new URLSearchParams(window.location.search).get('tab');
      setSelectedLeadId(decodeURIComponent(m[1]));
      setLeadTab(leadTabFromQuery(t));
      setCurrentPage('lead-detail');
      return true;
    };

    if (!openLeadFromPath(path)) {
      const { page, tab, who, when, rep, settingsTab: stab } = parseLocation();
      setCurrentPage(page);
      setClientsTab(tab);
      setFuWho(who);
      setFuWhen(when);
      setStatsRep(rep);
      setSettingsTab(stab);
      // Normalize legacy/implicit paths to the canonical URL without a history push,
      // keeping the list's own view params (a pasted or reloaded filtered list).
      window.history.replaceState({}, '', urlForPage(page, tab, who, when, rep, stab) + (page === 'leads' ? listExtras() : ''));
    }

    const onPop = () => {
      if (openLeadFromPath(window.location.pathname)) return;
      const next = parseLocation();
      setCurrentPage(next.page);
      setClientsTab(next.tab);
      setFuWho(next.who);
      setFuWhen(next.when);
      setStatsRep(next.rep);
      setSettingsTab(next.settingsTab);
      setSelectedLeadId(null);
      // Back/Forward onto a list is a return: rebuild it from the URL, scroll included.
      if (next.page === 'leads') { armListScroll(); setListNonce((n) => n + 1); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // THE BROWSER TITLE NAMES THE PAGE, so tabs and the Back-button history can
  // tell pages apart. The lead file sets its own (client name and job number,
  // LeadDetailPage). The client portals (every link a client is sent) read
  // just "Baltic Van Lines" — no "CRM", no internal page or lead name.
  const isClientPortal = /^\/(estimate|document)\//.test(window.location.pathname);
  const isPublicPath = isClientPortal || window.location.pathname === '/auth-setup';
  React.useEffect(() => {
    if (isClientPortal) { document.title = 'Baltic Van Lines'; return; }
    if (isPublicPath || !token || !user || currentPage === 'lead-detail') return;
    const name = currentPage === 'leads' ? CLIENTS_TAB_TITLES[clientsTab] : PAGE_TITLES[currentPage];
    document.title = `${name} – BVL CRM`;
  }, [isClientPortal, isPublicPath, token, user, currentPage, clientsTab]);

  // Any navigation closes the phone search row and the More sheet.
  React.useEffect(() => { setSearchOpen(false); setMoreOpen(false); }, [currentPage, clientsTab, selectedLeadId]);

  // PUBLIC estimate portal (Phase P1) — rendered BEFORE the auth gate so
  // customers open it from an emailed link with no login. Path: /estimate/<token>.
  const portalMatch = window.location.pathname.match(/^\/estimate\/([^/]+)\/?$/);
  if (portalMatch) {
    return <EstimatePortal token={decodeURIComponent(portalMatch[1])} />;
  }

  // PUBLIC document portal (E3 Item 4) — invoice / inventory / contractor links.
  const docPortalMatch = window.location.pathname.match(/^\/document\/([^/]+)\/?$/);
  if (docPortalMatch) {
    return <DocumentPortal token={decodeURIComponent(docPortalMatch[1])} />;
  }

  if (window.location.pathname === '/auth-setup') {
    return <AuthSetup />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--crm-bg)', color: 'var(--crm-text)' }}>
        Loading…
      </div>
    );
  }

  if (!token || !user) {
    return <LoginPage />;
  }

  // The month-end statement's print page (Accounting → Statements): a document,
  // outside the app shell. Admin only, like Accounting.
  const statementMatch = window.location.pathname.match(/^\/accounting\/statement\/(\d{4}-\d{2})\/?$/);
  if (statementMatch) {
    const q = new URLSearchParams(window.location.search);
    if (user.role !== 'admin') return <div style={{ padding: '40px', color: 'var(--crm-text)' }}>Accounting is admin-only.</div>;
    return q.get('view') === 'jobs'
      ? <JobReviewPrint month={statementMatch[1]} autoPrint={q.get('print') === '1'} />
      : <StatementPrint month={statementMatch[1]} autoPrint={q.get('print') === '1'} />;
  }

  // Key modals directly by type — no name mangling.
  const openModal = (type: string, data?: any) => {
    setModalData(data || {});
    setModals((m) => ({ ...m, [type]: true }));
  };
  const closeModal = (type: string) => setModals((m) => ({ ...m, [type]: false }));

  // Note the list view a lead is opened from (see lastListUrl). Opened from
  // anywhere else, the back link goes to a fresh list as before.
  const rememberList = () => {
    if (currentPage === 'leads') lastListUrl.current = window.location.pathname + window.location.search;
    else if (currentPage !== 'lead-detail') lastListUrl.current = null;
  };

  const handleSelectLead = (leadId: string) => {
    rememberList();
    setSelectedLeadId(leadId);
    setLeadTab('estimate'); // a freshly-opened lead starts on Estimate
    setCurrentPage('lead-detail');
    // Reflect the lead file in the URL so refresh / back-forward / paste land on
    // it (the openLeadFromPath reader was added in E3; this is the missing writer).
    window.history.pushState({}, '', `/lead/${encodeURIComponent(leadId)}`);
  };
  // Change the lead sub-tab, URL-synced (/lead/:id?tab=…); default tab omitted.
  // NOTE: a plain function (NOT useCallback) — this lives after the auth-gate early
  // returns above, so a hook here would violate hook order (React #310). Matches
  // handleSelectLead/handleBackFromLeadDetail, which are also plain by design.
  const changeLeadTab = (t: LeadTab) => {
    setLeadTab(t);
    if (selectedLeadId) {
      const url = `/lead/${encodeURIComponent(selectedLeadId)}` + (t !== 'estimate' ? `?tab=${t}` : '');
      window.history.pushState({}, '', url);
    }
  };
  // Open a lead's Sales feed at one email. Same URL shape as any other lead
  // link, so refresh and back behave normally; the message id is deliberately
  // NOT in the URL — "scroll here once" is an instruction, not a location.
  const openMessage = (leadId: string, messageId: string) => {
    rememberList();
    setSelectedLeadId(leadId);
    setLeadTab('sales');
    setCurrentPage('lead-detail');
    setFocusMessage((f) => ({ leadId, messageId, n: (f?.n ?? 0) + 1 }));
    window.history.pushState({}, '', `/lead/${encodeURIComponent(leadId)}?tab=sales`);
  };

  // Back to the list view the lead was opened from — its filters, page and
  // scroll — exactly as browser Back would.
  const handleBackFromLeadDetail = () => {
    setSelectedLeadId(null);
    if (!lastListUrl.current) { navigate('leads', clientsTab); return; }
    window.history.pushState({}, '', lastListUrl.current);
    const next = parseLocation();
    setCurrentPage('leads');
    setClientsTab(next.tab);
    setFuWho(next.who);
    setFuWhen(next.when);
    armListScroll();
    setListNonce((n) => n + 1);
  };

  const navButton = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '7px 14px',
    border: 'none',
    background: active ? 'var(--crm-rail-active-bg)' : 'transparent',
    color: active ? 'var(--crm-rail-active-text)' : 'var(--crm-rail-icon)',
    cursor: 'pointer',
    fontSize: '12px',
    borderLeft: active ? '2px solid var(--crm-rail-active)' : '2px solid transparent',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  });
  /** The rail item's icon: amber when it is the page you are on. */
  const railIcon = (active: boolean, icon: React.ReactNode) => (
    <span style={{ width: '18px', display: 'inline-flex', justifyContent: 'center', color: active ? 'var(--crm-rail-active)' : 'inherit' }}>{icon}</span>
  );
  const railProps = (active: boolean) => ({ className: 'crm-rail-item', 'aria-current': active ? ('page' as const) : undefined, style: navButton(active) });
  const labelSpan = (text: string) => <span style={{ opacity: navOpen ? 1 : 0, transition: 'opacity 0.15s' }}>{text}</span>;
  const sectionLabel = (text: string) => (
    <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--crm-rail-icon)', textTransform: 'uppercase', padding: '10px 14px', marginTop: '10px', opacity: navOpen ? 1 : 0, transition: 'opacity 0.15s', whiteSpace: 'nowrap' }}>{text}</div>
  );

  return (
    <div className="crm-app" style={{ display: 'flex', height: '100%', background: 'var(--crm-bg)', color: 'var(--crm-text)', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Sidebar (desktop; a phone gets the bottom bar below) */}
      {!phone && (
      <div
        onMouseEnter={() => setNavOpen(true)}
        onMouseLeave={() => setNavOpen(false)}
        style={{ width: navOpen ? '200px' : '48px', flexShrink: 0, background: 'var(--crm-rail)', borderRight: '1px solid var(--crm-rail-line)', display: 'flex', flexDirection: 'column', padding: '8px 0', overflow: 'hidden', transition: 'width 0.25s ease' }}
      >
        {/* The Baltic Van Lines logo, white on the navy rail ("MoveFlow" is the
            software, never the business — on a screen shown to Baltic's own
            client it read as somebody else's product). Collapsed, the rail is
            48px wide, so the mark alone stands in. */}
        <div style={{ height: '52px', padding: '0 8px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          {navOpen
            ? <img src="/brand/logo-horizontal-notag-white.svg" alt="Baltic Van Lines" style={{ height: '30px', width: 'auto', display: 'block' }} />
            : <img src="/brand/logo-mark-white.svg" alt="Baltic Van Lines" style={{ width: '32px', height: 'auto', display: 'block' }} />}
        </div>

        {sectionLabel('Pipeline')}
        {MAIN_NAV.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.id)}
            {...railProps(currentPage === item.id)}
          >
            {railIcon(currentPage === item.id, <item.Icon size={20} aria-hidden="true" />)}
            {labelSpan(item.label)}
          </button>
        ))}

        <div style={{ marginTop: 'auto' }} />

        {/* Settings is available to everyone (My Account is personal; company
            cards inside are admin-only + server-guarded). */}
        <button onClick={() => navigate('settings')} {...railProps(currentPage === 'settings')}>
          {railIcon(currentPage === 'settings', <Icons.settings size={20} aria-hidden="true" />)}
          {labelSpan('Settings')}
        </button>

        {user.role === 'admin' && (
          <>
            {sectionLabel('Admin')}
            <button onClick={() => navigate('accounting')} {...railProps(currentPage === 'accounting')}>
              {railIcon(currentPage === 'accounting', <Icons.money size={20} aria-hidden="true" />)}
              {labelSpan('Accounting')}
            </button>
            <button onClick={() => navigate('migration')} {...railProps(currentPage === 'migration')}>
              {railIcon(currentPage === 'migration', <Icons.folder size={20} aria-hidden="true" />)}
              {labelSpan('Migration')}
            </button>
          </>
        )}
      </div>
      )}

      {/* Right column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Top bar — phone: the title, a search icon that opens a full-width
            search row, and "+" for a new lead. Your name and Log out are in
            Settings. The lead file's own header names the lead. */}
        {phone && (
          <div style={{ background: 'var(--crm-topbar)', borderBottom: '1px solid var(--crm-topbar-line)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 6px 0 14px', height: '50px' }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {currentPage === 'leads' ? CLIENTS_TAB_TITLES[clientsTab] : PAGE_TITLES[currentPage]}
              </div>
              <button
                onClick={() => setSearchOpen((v) => !v)}
                aria-label={searchOpen ? 'Close search' : 'Search clients'}
                aria-expanded={searchOpen}
                style={{ width: '44px', height: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: searchOpen ? 'var(--crm-panel-2)' : 'transparent', border: 'none', borderRadius: '8px', color: 'var(--crm-text)', cursor: 'pointer' }}
              >
                {searchOpen ? <Icons.close size={20} aria-hidden="true" /> : <Icons.search size={20} aria-hidden="true" />}
              </button>
              <button
                onClick={() => openModal('addLead')}
                aria-label="New lead"
                style={{ width: '44px', height: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--crm-highlight)', border: 'none', borderRadius: '8px', color: 'var(--crm-on-highlight)', cursor: 'pointer', fontSize: '22px', fontWeight: 500, lineHeight: 1 }}
              >
                +
              </button>
            </div>
            {searchOpen && (
              <div style={{ padding: '0 10px 10px' }}>
                <GlobalSearch
                  fullWidth
                  autoFocus
                  onSelectLead={handleSelectLead}
                  onSeeAll={(q) => navigate('leads', 'all', false, { q })}
                />
              </div>
            )}
          </div>
        )}

        {/* Top bar (desktop) */}
        {!phone && (
        <div style={{ background: 'var(--crm-topbar)', borderBottom: '1px solid var(--crm-topbar-line)', padding: '0 16px', display: 'flex', alignItems: 'center', height: '50px', flexShrink: 0, justifyContent: 'space-between' }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{PAGE_TITLES[currentPage]}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <GlobalSearch
              onSelectLead={handleSelectLead}
              onSeeAll={(q) => navigate('leads', 'all', false, { q })}
            />
            <BadgeBar
              onSelectLead={handleSelectLead}
              onOpenMessage={openMessage}
              onGoToNewLeads={() => navigate('leads', 'new')}
              onGoToNotifications={() => navigate('leads', 'notifications')}
              onGoToFollowUps={() => navigate('leads', 'follow-ups')}
            />
            <button onClick={() => openModal('addLead')} style={{ padding: '6px 12px', background: 'var(--crm-highlight)', color: 'var(--crm-on-highlight)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>+ New lead</button>
            <div style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>{user.first_name} {user.last_name} ({user.role})</div>
            <button onClick={logout} style={{ padding: '6px 12px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Log out</button>
          </div>
        </div>
        )}

        {/* Page content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {currentPage === 'dashboard' && <Dashboard onSelectLead={handleSelectLead} onOpenFollowUps={(when) => goToFollowUps('mine', when)} />}
          {currentPage === 'leads' && (
            <LeadsPage
              key={listNonce}
              onSelectLead={handleSelectLead}
              onOpenModal={openModal}
              tab={clientsTab}
              onChangeTab={(t) => navigate('leads', t)}
              followupWho={fuWho}
              followupWhen={fuWhen}
              onChangeFollowupView={setFollowupView}
            />
          )}
          {currentPage === 'lead-detail' && selectedLeadId && <LeadDetailPage leadId={selectedLeadId} onBack={handleBackFromLeadDetail} onOpenModal={openModal} activeTab={leadTab} onChangeTab={changeLeadTab} focusMessage={focusMessage && focusMessage.leadId === selectedLeadId ? focusMessage : null} />}
          {currentPage === 'calendar' && <CalendarPage onSelectLead={handleSelectLead} />}
          {currentPage === 'stats' && <StatsPage rep={statsRep} onChangeRep={setStatsView} />}
          {currentPage === 'accounting' && user.role === 'admin' && <AccountingPage />}
          {currentPage === 'settings' && <SettingsPage activeTab={settingsTab} onTabChange={setSettingsView} />}
          {currentPage === 'migration' && user.role === 'admin' && <MigrationPage />}
        </div>

        {/* Bottom bar — phone only. Five 48px targets; More holds the pages a
            phone rarely needs. Safe-area padding keeps it clear of the home
            indicator. */}
        {phone && (
          <nav
            aria-label="Main"
            style={{ flexShrink: 0, display: 'flex', background: 'var(--crm-rail)', borderTop: '1px solid var(--crm-rail-line)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {([
              { key: 'dashboard', label: 'Dashboard', Icon: Icons.dashboard, active: currentPage === 'dashboard', go: () => navigate('dashboard') },
              { key: 'clients', label: 'Clients', Icon: Icons.clients, active: currentPage === 'leads' && clientsTab !== 'follow-ups', go: () => navigate('leads', 'all'), count: badgeCounts.newLeads + badgeCounts.notificationsUnread },
              { key: 'followups', label: 'Follow-Ups', Icon: Icons.followUp, active: currentPage === 'leads' && clientsTab === 'follow-ups', go: () => goToFollowUps(FU_WHO_DEFAULT, FU_WHEN_DEFAULT), count: followupBadgeOf(badgeCounts) },
              { key: 'calendar', label: 'Calendar', Icon: Icons.calendar, active: currentPage === 'calendar', go: () => navigate('calendar') },
              { key: 'more', label: 'More', Icon: null, active: moreOpen || ['stats', 'settings', 'accounting', 'migration'].includes(currentPage), go: () => setMoreOpen((v) => !v) },
            ] as const).map((t) => (
              <button
                key={t.key}
                onClick={t.go}
                className="crm-rail-item"
                aria-current={t.active ? 'page' : undefined}
                aria-label={'count' in t && t.count > 0 ? `${t.label}, ${t.count}` : undefined}
                style={{
                  flex: 1, minHeight: '56px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
                  background: 'transparent', border: 'none', borderTop: `2px solid ${t.active ? 'var(--crm-rail-active)' : 'transparent'}`,
                  color: t.active ? 'var(--crm-rail-active-text)' : 'var(--crm-rail-icon)', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex', color: t.active ? 'var(--crm-rail-active)' : 'inherit' }}>
                  {t.Icon ? <t.Icon size={20} aria-hidden="true" /> : <span aria-hidden="true" style={{ fontSize: '18px', lineHeight: '20px', letterSpacing: '1px' }}>•••</span>}
                  {'count' in t && t.count > 0 && (
                    <span style={{ position: 'absolute', top: '-6px', left: '13px', minWidth: '17px', height: '17px', padding: '0 4px', borderRadius: '9px', background: 'var(--crm-badge)', color: 'var(--crm-badge-text)', fontSize: '10px', fontWeight: 700, lineHeight: '17px', textAlign: 'center' }}>
                      {t.count > 99 ? '99+' : t.count}
                    </span>
                  )}
                </span>
                {t.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* More — phone only: the rest of the sidebar, as a sheet above the bottom bar. */}
      {phone && moreOpen && (
        <div onClick={() => setMoreOpen(false)} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 250 }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'var(--crm-panel)', borderTop: '1px solid var(--crm-line)', borderRadius: '14px 14px 0 0', padding: '8px 0 calc(64px + env(safe-area-inset-bottom))' }}
          >
            {([
              { id: 'stats' as PageType, label: 'Activity Stats', Icon: Icons.stats, show: true },
              { id: 'settings' as PageType, label: 'Settings & account', Icon: Icons.settings, show: true },
              { id: 'accounting' as PageType, label: 'Accounting', Icon: Icons.money, show: user.role === 'admin' },
              { id: 'migration' as PageType, label: 'Migration', Icon: Icons.folder, show: user.role === 'admin' },
            ]).filter((m) => m.show).map((m) => (
              <button
                key={m.id}
                onClick={() => { setMoreOpen(false); navigate(m.id); }}
                style={{ width: '100%', minHeight: '52px', display: 'flex', alignItems: 'center', gap: '14px', padding: '0 20px', background: currentPage === m.id ? 'var(--crm-panel-2)' : 'transparent', border: 'none', color: 'var(--crm-text)', cursor: 'pointer', fontSize: '15px', textAlign: 'left' }}
              >
                <m.Icon size={20} aria-hidden="true" /> {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {/* refreshShared() is required: without it a lead created here stays
          invisible to the top-bar search until a full page reload. */}
      <AddLeadModal isOpen={!!modals.addLead} onClose={() => closeModal('addLead')} onSuccess={() => { refreshShared(); navigate('leads', 'all'); }} />
      <AddBranchModal isOpen={!!modals.addBranch} onClose={() => closeModal('addBranch')} />
      <AddTariffModal isOpen={!!modals.addTariff} onClose={() => closeModal('addTariff')} />
      <AddFeeModal isOpen={!!modals.addFee} onClose={() => closeModal('addFee')} />
      {/* Follow-up scheduling now lives in the Sales-tab ActivityLog composer (8h.3). */}

      {/* RingCentral phone (plan step 1: docked, events logged, nothing else). */}
      <RingCentralDock />
    </div>
  );
}

function LoginPage() {
  const { login, error, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch {
      // Error surfaced via auth context.
    }
  };

  const field: React.CSSProperties = { width: '100%', padding: '10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px', color: 'var(--crm-text)', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--crm-bg)' }}>
      <div style={{ width: '100%', maxWidth: '400px', padding: '40px', background: 'var(--crm-panel)', borderRadius: '10px', border: '1px solid var(--crm-line)' }}>
        <h1 style={{ margin: '0 0 20px' }}>
          <img src="/brand/logo-horizontal-color.svg" alt="Baltic Van Lines" style={{ height: '56px', width: 'auto', display: 'block' }} />
        </h1>
        <p style={{ color: 'var(--crm-muted)', marginBottom: '20px', fontSize: '14px' }}>Sign in to continue</p>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '15px' }}>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} autoComplete="username" style={field} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} autoComplete="current-password" style={field} />
          </div>
          {error && <div style={{ marginBottom: '15px', padding: '10px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '13px' }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '10px', background: loading ? 'var(--crm-line)' : 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500 }}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
